import clsx from 'clsx'
import { type ComponentPropsWithRef, type ElementType } from 'react'

type TextType =
    | 'heading1'
    | 'heading2'
    | 'heading3'
    | 'heading4'
    | 'heading5'
    | 'heading6'
    | 'paragraph'
    | 'small'
    | 'xs'
    | 'title1'
    | 'title2'
    | 'title3'
    | 'subtitle1'
    | 'subtitle2'
    | 'body1'
    | 'body2'
    | 'caption'

const textType: Record<TextType, ElementType> = {
    heading1: 'h1',
    heading2: 'h2',
    heading3: 'h3',
    heading4: 'h4',
    heading5: 'h5',
    heading6: 'h6',
    paragraph: 'p',
    small: 'small',
    xs: 'span',
    title1: 'span',
    title2: 'span',
    title3: 'span',
    subtitle1: 'span',
    subtitle2: 'span',
    body1: 'span',
    body2: 'span',
    caption: 'span',
}

const hardStyle = 'font-bold text-neutral-800 dark:text-neutral-50'
const mediumStyle = 'font-semibold text-neutral-800 dark:text-neutral-50'
const softStyle = 'text-neutral-600 dark:text-neutral-400'

const textClass: Record<TextType, string> = {
    heading1: clsx('text-4xl', hardStyle),
    heading2: clsx('text-3xl', hardStyle),
    heading3: clsx('text-2xl', hardStyle),
    heading4: clsx('text-xl', hardStyle),
    heading5: clsx('text-lg', hardStyle),
    heading6: clsx('text-base', hardStyle),
    paragraph: clsx('text-base', softStyle),
    small: clsx('text-sm', softStyle),
    xs: clsx('text-xs', softStyle),
    title1: clsx('text-2xl', hardStyle),
    title2: clsx('text-xl', mediumStyle),
    title3: clsx('text-lg', mediumStyle),
    subtitle1: clsx('text-lg', softStyle),
    subtitle2: clsx('text-base', softStyle),
    body1: clsx('text-base', softStyle),
    body2: clsx('text-sm', softStyle),
    caption: clsx('text-xs', softStyle),
}

type TextProps = ComponentPropsWithRef<'span'> & {
    type?: TextType
    as?: ElementType
}

const Text = (props: TextProps) => {
    const { type = 'body1', as, children, className, ...otherProps } = props

    const Component = as ?? textType[type]

    return (
        <Component className={clsx(textClass[type], className)} {...otherProps}>
            {children}
        </Component>
    )
}

Text.displayName = 'Text'

export { Text, type TextProps }
